/**
 * 全部激活二次确认弹窗（spec 11.18，参考电商复刻视觉语言）
 *
 * - 打开时拉 previewActivateAll：将激活 / 跳过已激活 / 跳过参考素材未就绪
 * - 显示估算积分总计与当前用户积分余额，余额不足时禁用按钮
 */

import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Coins, Loader2, Wand2, X, Sparkles } from 'lucide-react';
import toast from '../../../../components/Toast';
import { directorApi, DirectorApiError, type ActivateAllPreview } from '../../services/directorApi';

interface Props {
  open: boolean;
  projectId: string;
  currentCredits: number;
  onClose: () => void;
  onSubmitted: (result: {
    videoJobIds: string[];
    queuedCount: number;
    creditsUsed: number;
  }) => void;
}

export function ActivateAllDialog({
  open,
  projectId,
  currentCredits,
  onClose,
  onSubmitted,
}: Props) {
  const [preview, setPreview] = useState<ActivateAllPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setLoadingPreview(true);
    directorApi
      .previewActivateAll(projectId)
      .then(setPreview)
      .catch((err) => {
        toast.error(err instanceof DirectorApiError ? err.message : '预览失败');
        onClose();
      })
      .finally(() => setLoadingPreview(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  if (!open) return null;

  const totalCredits = preview?.estimatedCreditsTotal ?? 0;
  const insufficient = totalCredits > currentCredits;
  const canSubmit = !!preview && preview.toActivateCount > 0 && !insufficient && !submitting;

  const handleSubmit = async () => {
    if (!preview) return;
    setSubmitting(true);
    try {
      const result = await directorApi.activateAll(projectId);
      onSubmitted({
        videoJobIds: result.videoJobIds,
        queuedCount: result.queuedCount,
        creditsUsed: result.creditsUsed,
      });
      onClose();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '激活失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="flex w-full max-w-md flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30">
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white">
          <div className="absolute right-[-40px] top-[-40px] h-32 w-32 rounded-full bg-sky-400/20 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-200">
                <Sparkles className="h-3 w-3 text-sky-200" />
                批量激活
              </div>
              <h2 className="text-lg font-semibold leading-tight">全部激活分镜</h2>
              <p className="mt-1 text-xs text-slate-300">
                自动跳过已激活和参考素材未就绪的分镜
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-4 bg-slate-50 p-5">
          {loadingPreview || !preview ? (
            <div className="flex items-center justify-center py-8 text-sm text-slate-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin text-sky-500" />
              加载分镜统计…
            </div>
          ) : (
            <>
              {/* 分类列表 */}
              <div className="space-y-2">
                <Row
                  icon={<Wand2 className="h-4 w-4 text-sky-500" />}
                  label="将激活"
                  count={preview.toActivateCount}
                  emphasized
                />
                <Row
                  icon={<CheckCircle2 className="h-4 w-4 text-slate-400" />}
                  label="跳过（已激活）"
                  count={preview.skippedActiveCount}
                  muted
                />
                <Row
                  icon={<AlertCircle className="h-4 w-4 text-slate-400" />}
                  label="跳过（参考素材未就绪）"
                  count={preview.skippedNoKeyframeCount}
                  muted
                />
              </div>

              {/* 积分 */}
              <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-400">
                    <Coins className="h-3 w-3" /> 预计消耗
                  </div>
                  <div className="text-base font-semibold text-sky-700">{totalCredits} 积分</div>
                </div>
                <div className="mt-2 flex items-center justify-between text-[11px]">
                  <span className="text-slate-400">当前余额</span>
                  <span className={insufficient ? 'text-rose-500' : 'text-slate-600'}>
                    {currentCredits} 积分
                    {insufficient && <span className="ml-2">（不足）</span>}
                  </span>
                </div>
              </div>

              {preview.toActivateCount === 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                  当前没有可激活的分镜。请先准备关键帧、帧序列或三合一卡，或取消已有任务。
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            确认激活
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({
  icon,
  label,
  count,
  emphasized,
  muted,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  emphasized?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between rounded-2xl px-3 py-2 ${
        emphasized ? 'bg-sky-50 ring-1 ring-sky-100' : 'bg-white ring-1 ring-slate-100'
      }`}
    >
      <div
        className={`flex items-center gap-2 text-sm ${muted ? 'text-slate-500' : 'text-slate-700'}`}
      >
        {icon}
        <span>{label}</span>
      </div>
      <div
        className={`text-base font-semibold ${
          emphasized ? 'text-sky-700' : muted ? 'text-slate-400' : 'text-slate-900'
        }`}
      >
        {count}
      </div>
    </div>
  );
}
